import { routes } from '@semios/app-platform-banyan-route-definitions'
import { fetchValuesData } from 'App/Map/PanelDetails/_utils/useApiValuesTimeseries/fetchValuesData'
import type { TLngLat } from 'App/Map/types'
import { translate } from 'i18n/i18n'
import moment from 'moment-timezone'
import { useCallback } from 'react'
import { apiFetch } from 'utils/apiFetch'
import { showNotification } from 'utils/showNotification'

export const useSoilCalibrationData = (
  dates: [Date | null, Date | null],
  data: NonNullable<routes.Values.Response['points']>,
  setData: (data: NonNullable<routes.Values.Response['points']>) => void,
  setOverrides: (overrides: routes.SoilCalibrationSettingsGet.Response['overrides']) => void,
  setDefaultLastGoodReading: (reading: routes.SoilCalibrationSettingsGet.Response['lastGoodReading']) => void,
  setLastGoodReading: (reading: routes.SoilCalibrationSettingsGet.Response['lastGoodReading']) => void,
  setLoading: (loading: boolean) => void,
) => {
  const fetchSoilData = useCallback(
    async (
      newStation: TLngLat,
      newDates?: [Date | null, Date | null],
      onDataLoaded?: (overrides: routes.SoilCalibrationSettingsGet.Response['overrides']) => void,
    ) => {
      const datesToUse = newDates || dates

      if (!datesToUse[0] || !datesToUse[1]) {
        return
      }

      setLoading(true)

      try {
        const timezone = moment.tz.guess()
        const dateFrom = moment.tz(datesToUse[0].toISOString(), timezone).startOf('day').toISOString()
        const dateTo = moment.tz(datesToUse[1].toISOString(), timezone).endOf('day').toISOString()

        const values = await fetchValuesData(
          {
            points: {
              lngLats: [newStation],
              valuesRequested: { soilVwc: { preferredAggregationInterval: 'HOURLY' } },
            },
            dateFrom,
            dateTo,
          },
          data,
        )

        setData(values?.points ?? {})

        const response = await apiFetch({
          url: routes.SoilCalibrationSettingsGet.path,
          body: {
            dateFrom,
            dateTo,
            lngLat: newStation,
          },
        })

        setOverrides(response.overrides)

        setDefaultLastGoodReading(response.lastGoodReading)

        setLastGoodReading(response.lastGoodReading)

        if (onDataLoaded) {
          onDataLoaded(response.overrides)
        }
      } catch (error) {
        showNotification({
          message: translate.phrases.banyanApp(
            'Failed to fetch {{label}}. Please re-open the {{label}} modal to try again',
            {
              label: translate.phrases.banyanApp('Soil Calibration Settings'),
            },
          ),
          type: 'error',
        })

        // eslint-disable-next-line no-console
        console.error(error)
      } finally {
        setLoading(false)
      }
    },
    [dates, data, setData, setOverrides, setDefaultLastGoodReading, setLastGoodReading, setLoading],
  )

  return { fetchSoilData }
}
